import { useCallback, useRef, useEffect } from 'react';

/**
 * NumberStepper Component
 *
 * A branded number input with +/- stepper buttons.
 * Great for compact number selection like timers, quantities, etc.
 *
 * Features:
 * - Press and hold for continuous increment/decrement
 * - Accelerates when holding longer
 */

export interface NumberStepperProps {
  /** Current value */
  value: number;
  /** Called when value changes */
  onChange: (value: number) => void;
  /** Minimum allowed value */
  min?: number;
  /** Maximum allowed value */
  max?: number;
  /** Step increment/decrement amount */
  step?: number;
  /** Suffix to display after the number (e.g., "m", "px", "%") */
  suffix?: string;
  /** Size variant */
  size?: 'sm' | 'md';
  /** Color variant */
  variant?: 'light' | 'dark';
  /** Disable the stepper */
  disabled?: boolean;
  /** Additional class names */
  className?: string;
}

export const NumberStepper = ({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  suffix = '',
  size = 'md',
  variant = 'light',
  disabled = false,
  className = '',
}: NumberStepperProps) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const holdCountRef = useRef(0);

  const decrement = useCallback(() => {
    onChange(Math.max(min, value - step));
  }, [min, step, value, onChange]);

  const increment = useCallback(() => {
    onChange(Math.min(max, value + step));
  }, [max, step, value, onChange]);

  // Clear all timers
  const clearTimers = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    holdCountRef.current = 0;
  }, []);

  // Start hold behavior
  const startHold = useCallback((action: () => void) => {
    // Immediate first action
    action();
    holdCountRef.current = 0;

    // Start repeating after initial delay
    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        action();
        holdCountRef.current++;

        // Accelerate: after 5 ticks, go faster
        if (holdCountRef.current === 5 && intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = setInterval(action, 50);
        }
      }, 150); // Initial repeat speed
    }, 400); // Delay before repeat starts
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return clearTimers;
  }, [clearTimers]);

  // Size classes
  const sizeClasses = {
    sm: {
      button: 'px-1.5 py-0.5 text-sm',
      value: 'px-2 py-0.5 text-xs min-w-[2.5rem]',
    },
    md: {
      button: 'px-2 py-1 text-base',
      value: 'px-3 py-1 text-sm min-w-[3rem]',
    },
  };

  // Variant classes
  const variantClasses = {
    light: {
      container: 'bg-gray-100 border border-gray-200',
      button: 'text-gray-500 hover:text-gray-700 hover:bg-gray-200',
      buttonDisabled: 'text-gray-300 cursor-not-allowed',
      value: 'text-gray-900',
    },
    dark: {
      container: 'bg-white/10',
      button: 'text-white/70 hover:text-white hover:bg-white/10',
      buttonDisabled: 'text-white/30 cursor-not-allowed',
      value: 'text-white',
    },
  };

  const sizes = sizeClasses[size];
  const colors = variantClasses[variant];

  const canDecrement = !disabled && value > min;
  const canIncrement = !disabled && value < max;

  return (
    <div
      className={`inline-flex items-center rounded-lg ${colors.container} ${disabled ? 'opacity-50' : ''} ${className}`}
    >
      <button
        type="button"
        onMouseDown={() => canDecrement && startHold(decrement)}
        onMouseUp={clearTimers}
        onMouseLeave={clearTimers}
        onTouchStart={() => canDecrement && startHold(decrement)}
        onTouchEnd={clearTimers}
        disabled={!canDecrement}
        className={`${sizes.button} rounded-l-lg transition-colors select-none ${
          canDecrement ? colors.button : colors.buttonDisabled
        }`}
        aria-label="Decrease"
      >
        −
      </button>
      <span
        className={`${sizes.value} font-semibold text-center tabular-nums ${colors.value}`}
      >
        {value}{suffix}
      </span>
      <button
        type="button"
        onMouseDown={() => canIncrement && startHold(increment)}
        onMouseUp={clearTimers}
        onMouseLeave={clearTimers}
        onTouchStart={() => canIncrement && startHold(increment)}
        onTouchEnd={clearTimers}
        disabled={!canIncrement}
        className={`${sizes.button} rounded-r-lg transition-colors select-none ${
          canIncrement ? colors.button : colors.buttonDisabled
        }`}
        aria-label="Increase"
      >
        +
      </button>
    </div>
  );
};
